import * as duckdb from '@duckdb/duckdb-wasm'
import {DuckDBDataProtocol} from '@duckdb/duckdb-wasm'
import duckdb_wasm from '@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm?url'
import mvp_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js?url'
import duckdb_wasm_eh from '@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url'
import eh_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url'
import {post} from '@/services/infrastructure/api.service'
import {computed, ref} from 'vue'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {storageResolveUrl} from '@/services/document.service'
import {IServerSideGetRowsRequest} from 'ag-grid-community'
import {findDocumentMeta} from '@/document/documentMeta.service'
import {queryFromAggrid} from '@/agUtils/sql'

const MANUAL_BUNDLES: duckdb.DuckDBBundles = {
    mvp: {
        mainModule: duckdb_wasm,
        mainWorker: mvp_worker
    },
    eh: {
        mainModule: duckdb_wasm_eh,
        mainWorker: eh_worker
    }
}
// Select a bundle based on browser checks
const bundle = await duckdb.selectBundle(MANUAL_BUNDLES)
// Instantiate the asynchronous version of DuckDB-wasm
const worker = new Worker(bundle.mainWorker!)
const logger = new duckdb.ConsoleLogger()
const db = new duckdb.AsyncDuckDB(logger, worker)
await db.instantiate(bundle.mainModule, bundle.pthreadWorker)
const conn = await db.connect()

export type Column = {
    column_name: string
    column_type: string
}

export const registerParquetFile = async (name: string, url: string) => {
    await db.registerFileURL(name, url, DuckDBDataProtocol.HTTP, false)
}

export const getLatestSnapshot = async (): Promise<DocumentMeta | undefined> => {
    const request: IServerSideGetRowsRequest = {
        startRow: 0,
        endRow: 1,
        filterModel: {contentType: {filterType: 'text', type: 'equals', filter: 'application/parquet'}},
        sortModel: [{colId: 'createdAt', sort: 'desc'}],
        rowGroupCols: [],
        valueCols: [],
        pivotCols: [],
        pivotMode: false,
        groupKeys: []
    }

    const response = await findDocumentMeta(request)
    return response.length ? response[0] : undefined
}

export const useDuckDB = () => {
    const documentMeta = ref<DocumentMeta>()
    const tableName = computed(() => {
        return documentMeta.value?.filename
    })

    const getColumns = async (): Promise<Column[]> => {
        if (!tableName.value) return []
        const result = await conn.query(`describe ${tableName.value}`)

        return result.toArray().map(x => x.toJSON()) as Column[]
    }

    const internalColumns = ref<Column[]>([])
    const columns = computed<Column[]>(() => internalColumns.value)

    const query = async (request: IServerSideGetRowsRequest) => {
        if (!tableName.value) return []
        const queryString = queryFromAggrid(tableName.value, request)
        return (await conn.query(queryString)).toArray().map(x => x.toJSON())
    }

    const newSnapshot = async () => {
        documentMeta.value = await post('/snapshot', {})
        if (documentMeta.value && tableName.value) {
            const url = await storageResolveUrl(documentMeta.value)
            await registerParquetFile(tableName.value, url)
            internalColumns.value = await getColumns()
        }
    }

    const init = async () => {
        documentMeta.value = await getLatestSnapshot()
        if (documentMeta.value && tableName.value) {
            const url = await storageResolveUrl(documentMeta.value)
            await registerParquetFile(tableName.value, url)
            internalColumns.value = await getColumns()
        }
    }

    const clear = async () => {
        documentMeta.value = undefined
        internalColumns.value = []
        await db.dropFiles()
    }

    return {
        columns,
        query,
        newSnapshot,
        clear,
        init,
        documentMeta,
        tableName
    }
}